import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Plus } from 'lucide-react';

export interface ParsingFieldSelection {
  field_id: string;
  field_name: string;
  field_type: string;
  is_selected: boolean;
  is_mandatory: boolean;
  is_required: boolean;
}

interface ParsingTabProps {
  parsingFields: ParsingFieldSelection[];
  setParsingFields: (fields: ParsingFieldSelection[]) => void;
  applyToProperty: boolean;
  setApplyToProperty: (apply: boolean) => void;
}

// Default 12 fields as specified
const DEFAULT_FIELDS = [
  { field_name: 'Invoice Date', field_type: 'date', is_mandatory: true },
  { field_name: 'Invoice Number', field_type: 'text', is_mandatory: true },
  { field_name: 'Billing Period From', field_type: 'date', is_mandatory: true },
  { field_name: 'Billing Period To', field_type: 'date', is_mandatory: true },
  { field_name: 'Account Number', field_type: 'text', is_mandatory: true },
  { field_name: 'Previous Payment', field_type: 'currency', is_mandatory: false },
  { field_name: 'Previous Due', field_type: 'currency', is_mandatory: false },
  { field_name: 'Late Fee', field_type: 'currency', is_mandatory: false },
  { field_name: 'Total Current Charges', field_type: 'currency', is_mandatory: true },
  { field_name: 'Unit Rate', field_type: 'number', is_mandatory: false },
  { field_name: 'Consumption', field_type: 'number', is_mandatory: false },
  { field_name: 'Total Amount Due', field_type: 'currency', is_mandatory: true },
];

export const ParsingTab: React.FC<ParsingTabProps> = ({
  parsingFields,
  setParsingFields,
  applyToProperty,
  setApplyToProperty,
}) => {
  const [addToTemplate, setAddToTemplate] = useState(true);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [showAddField, setShowAddField] = useState(false);

  // Initialize parsing fields with defaults if empty
  useEffect(() => {
    if (parsingFields.length === 0) {
      const initialFields: ParsingFieldSelection[] = DEFAULT_FIELDS.map((f, idx) => ({
        field_id: `default_${idx}`,
        field_name: f.field_name,
        field_type: f.field_type,
        is_selected: true,
        is_mandatory: f.is_mandatory,
        is_required: f.is_mandatory,
      }));
      setParsingFields(initialFields);
    }
  }, [parsingFields.length, setParsingFields]);

  // Combine default and custom fields
  const customFields = parsingFields.filter(f => !f.field_id.startsWith('default_'));

  const handleToggleField = (fieldId: string) => {
    setParsingFields(
      parsingFields.map(f => 
        f.field_id === fieldId ? { ...f, is_selected: !f.is_selected } : f
      )
    );
  };

  const handleToggleRequired = (fieldId: string, required: boolean) => {
    setParsingFields(
      parsingFields.map(f => 
        f.field_id === fieldId ? { ...f, is_required: required } : f
      )
    );
  };

  const handleDeleteCustomField = (fieldId: string) => {
    setParsingFields(parsingFields.filter(f => f.field_id !== fieldId));
  };

  const handleAddCustomField = () => {
    if (!newFieldName.trim()) return;

    const newField: ParsingFieldSelection = {
      field_id: `custom_${Date.now()}`,
      field_name: newFieldName.trim(),
      field_type: newFieldType,
      is_selected: true,
      is_mandatory: false,
      is_required: newFieldRequired,
    };

    setParsingFields([...parsingFields, newField]);
    setNewFieldName('');
    setNewFieldType('text');
    setNewFieldRequired(false);
    setShowAddField(false);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h4 className="font-medium text-sm">Fields to Extract from Bills</h4>
        <p className="text-xs text-muted-foreground">
          Select the fields that should be extracted from bills for this account.
        </p>
      </div>

      {/* Default Fields */}
      <Card>
        <CardContent className="pt-4">
          <h5 className="text-sm font-medium mb-3">Default Fields (Pre-selected):</h5>
          <div className="space-y-2">
            {parsingFields.filter(f => f.field_id.startsWith('default_')).map((field) => (
              <div key={field.field_id} className="flex items-center justify-between py-1">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={field.field_id}
                    checked={field.is_selected}
                    onCheckedChange={() => handleToggleField(field.field_id)}
                    disabled={field.is_mandatory}
                  />
                  <Label htmlFor={field.field_id} className="font-normal text-sm">
                    {field.field_name}
                    {field.is_mandatory && <span className="text-destructive ml-1">*</span>}
                  </Label>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add Custom Field Button */}
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => setShowAddField(!showAddField)}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Custom Field
      </Button>

      {/* Add Custom Field Form */}
      {showAddField && (
        <Card className="border-dashed">
          <CardContent className="pt-4 space-y-4">
            <h5 className="text-sm font-medium">Add Custom Field</h5>
            
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Field Name</Label>
                <Input
                  placeholder="e.g., Service Charge"
                  value={newFieldName}
                  onChange={(e) => setNewFieldName(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">Field Type</Label>
                <Select value={newFieldType} onValueChange={setNewFieldType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="currency">Currency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Required</Label>
                <RadioGroup
                  value={newFieldRequired ? 'yes' : 'no'}
                  onValueChange={(v) => setNewFieldRequired(v === 'yes')}
                  className="flex gap-4 pt-2"
                >
                  <div className="flex items-center space-x-1">
                    <RadioGroupItem value="yes" id="req_yes" />
                    <Label htmlFor="req_yes" className="text-xs font-normal">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-1">
                    <RadioGroupItem value="no" id="req_no" />
                    <Label htmlFor="req_no" className="text-xs font-normal">No</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            <Button size="sm" onClick={handleAddCustomField}>
              Add Field
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Custom Fields List */}
      {customFields.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h5 className="text-sm font-medium mb-3">Custom Fields:</h5>
            <div className="space-y-2">
              {customFields.map((field) => (
                <div key={field.field_id} className="flex items-center justify-between py-1">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={field.field_id}
                      checked={field.is_selected}
                      onCheckedChange={() => handleToggleField(field.field_id)}
                    />
                    <Label htmlFor={field.field_id} className="font-normal text-sm">
                      {field.field_name}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      value={field.is_required ? 'required' : 'optional'}
                      onValueChange={(v) => handleToggleRequired(field.field_id, v === 'required')}
                    >
                      <SelectTrigger className="w-[100px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="required">Required</SelectItem>
                        <SelectItem value="optional">Optional</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleDeleteCustomField(field.field_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bottom Checkboxes */}
      <div className="space-y-3 pt-4 border-t">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="apply_parsing_property"
            checked={applyToProperty}
            onCheckedChange={(checked) => setApplyToProperty(!!checked)}
          />
          <Label htmlFor="apply_parsing_property" className="text-sm font-normal">
            Apply these parsing rules to all accounts under this property
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="add_to_template"
            checked={addToTemplate}
            onCheckedChange={(checked) => setAddToTemplate(!!checked)}
          />
          <Label htmlFor="add_to_template" className="text-sm font-normal">
            Add these fields to the bill template
          </Label>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        * = Mandatory fields that cannot be removed
      </p>
    </div>
  );
};
